<template>
  <div>
    <!-- Loading -->
    <template v-if="loading">
      <div class="m-container py-8">
        <MSkeletonLoader type="text" :width="300" class="mb-4" />
        <MSkeletonLoader type="heading" class="mb-4" />
        <MSkeletonLoader type="text" class="mb-2" />
        <MSkeletonLoader type="image" :height="400" class="mb-6" />
        <MSkeletonLoader type="paragraph" class="mb-2" />
        <MSkeletonLoader type="paragraph" class="mb-2" />
        <MSkeletonLoader type="paragraph" />
      </div>
    </template>

    <template v-else-if="article">
      <!-- Hero -->
      <section v-if="article.image" class="article-hero">
        <MImg :src="article.image" :height="400" cover :lazy="false" class="article-hero__img" />
        <div class="article-hero__overlay" />
      </section>

      <article class="m-container py-8">
        <div class="m-row" style="justify-content: center">
          <div class="m-col m-col-12 m-col-md-8">
            <!-- Breadcrumbs -->
            <MBreadcrumbs :items="breadcrumbs" class="mb-4" />

            <!-- Header -->
            <div class="mb-6">
              <MChip :color="topicColor" size="small" variant="tonal" class="mb-3 text-none font-weight-bold">
                {{ topicLabel }}
              </MChip>
              <h1 class="article-page__title">{{ article.title }}</h1>
              <div class="d-flex align-center ga-4 mt-3 text-medium-emphasis">
                <span v-if="article.author?.name" class="d-flex align-center ga-1">
                  <MIcon icon="account" :size="16" />
                  {{ article.author.name }}
                </span>
                <span class="d-flex align-center ga-1">
                  <MIcon icon="clock-outline" :size="16" />
                  {{ article.readTime }} min Lesezeit
                </span>
                <span class="d-flex align-center ga-1">
                  <MIcon icon="calendar" :size="16" />
                  {{ formatDate(article.createdAt) }}
                </span>
              </div>
            </div>

            <!-- Excerpt -->
            <p class="article-page__excerpt">{{ article.excerpt }}</p>

            <hr class="m-divider my-6" />

            <!-- Content -->
            <div class="article-page__content" v-html="renderedContent" />

            <!-- Verknüpfte Produkte -->
            <section v-if="relatedProducts.length" class="mt-8">
              <hr class="m-divider mb-6" />
              <h2 class="titel-h2 font-weight-bold mb-4">Passende Produkte</h2>
              <div class="m-row">
                <div v-for="p in relatedProducts" :key="p.id" class="m-col m-col-6 m-col-sm-4 m-col-md-3">
                  <ProductCard :product="p" />
                </div>
              </div>
            </section>

            <!-- Navigation -->
            <div class="d-flex justify-space-between mt-8">
              <NuxtLink to="/magazin" class="d-flex align-center ga-1 text-decoration-none" style="color: var(--m-primary)">
                <MIcon icon="arrow-left" :size="18" />
                Zurück zum Magazin
              </NuxtLink>
            </div>
          </div>
        </div>
      </article>
    </template>

    <!-- Nicht gefunden -->
    <div v-else class="m-container py-16 text-center">
      <MIcon icon="book-off-outline" :size="64" color="var(--m-border)" class="mb-4" />
      <h2 class="text-h6 text-medium-emphasis mb-2">Artikel nicht gefunden</h2>
      <MBtn color="primary" variant="flat" rounded to="/magazin" class="mt-4">
        Zum Magazin
      </MBtn>
    </div>
  </div>
</template>

<script setup lang="ts">
const api = useApi();
const route = useRoute();
const slug = computed(() => route.params.slug as string);

const loading = ref(true);
const article = ref<any>(null);
const relatedProducts = ref<any[]>([]);

const topicMap: Record<string, { label: string; color: string }> = {
  kaufberatung: { label: "Kaufberatung", color: "primary" },
  einrichtung: { label: "Einrichtung", color: "info" },
  pflege: { label: "Pflege & Tipps", color: "success" },
  trends: { label: "Trends", color: "secondary" },
};

const topicLabel = computed(() => topicMap[article.value?.topic]?.label ?? article.value?.topic ?? "");
const topicColor = computed(() => topicMap[article.value?.topic]?.color ?? "primary");

const breadcrumbs = computed(() => [
  { title: "Home", to: "/" },
  { title: "Magazin", to: "/magazin" },
  { title: article.value?.title || "Artikel" },
]);

const formatDate = (d: string) => new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });

const renderedContent = computed(() => {
  if (!article.value?.content) return "";
  let md = article.value.content;
  md = md.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  md = md.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  md = md.replace(/^# (.+)$/gm, '<h1>$1</h1>');
  md = md.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  md = md.replace(/\*(.+?)\*/g, '<em>$1</em>');
  md = md.replace(/^\d+\.\s+(.+)$/gm, '<li class="ol-item">$1</li>');
  md = md.replace(/^- (.+)$/gm, '<li>$1</li>');
  md = md.replace(/(<li>[\s\S]*?<\/li>)\n(?!<li)/g, '$1</ul>\n');
  md = md.replace(/(?<!<\/ul>)\n(<li>)/g, '\n<ul>$1');
  md = md.replace(/\n\n+/g, '</p>\n<p>');
  md = `<p>${md}</p>`;
  md = md.replace(/<p>\s*(<h[1-3]>)/g, '$1');
  md = md.replace(/(<\/h[1-3]>)\s*<\/p>/g, '$1');
  md = md.replace(/<p>\s*<ul>/g, '<ul>');
  md = md.replace(/<\/ul>\s*<\/p>/g, '</ul>');
  md = md.replace(/<p>\s*<\/p>/g, '');
  return md;
});

async function loadArticle() {
  loading.value = true;
  try {
    const data = await api<any>(`/api/magazine/${slug.value}`);
    article.value = data.article;
    relatedProducts.value = data.relatedProducts || [];
  } catch {
    article.value = null;
  } finally {
    loading.value = false;
  }
}

onMounted(loadArticle);
watch(slug, loadArticle);

// SEO
const seoTitle = computed(() => {
  if (!article.value) return "Artikel | Moebix Magazin";
  return article.value.metaTitle || `${article.value.title} | Moebix Magazin`;
});

const seoDesc = computed(() => {
  if (!article.value) return "";
  return article.value.metaDescription || article.value.excerpt;
});

useSeoMeta({
  title: seoTitle,
  description: seoDesc,
  ogTitle: computed(() => article.value?.title || "Moebix Magazin"),
  ogDescription: seoDesc,
  ogImage: computed(() => article.value?.image || undefined),
  ogType: "article",
  twitterCard: "summary_large_image",
});

useHead({
  link: [{ rel: "canonical", href: computed(() => `https://www.moebix.de/magazin/${slug.value}`) }],
  script: computed(() => {
    if (!article.value) return [];
    return [{
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.value.title,
        description: article.value.excerpt,
        image: article.value.image || undefined,
        datePublished: article.value.createdAt,
        dateModified: article.value.updatedAt,
        author: article.value.author?.name ? { "@type": "Person", name: article.value.author.name } : { "@type": "Organization", name: "Moebix.de" },
        publisher: { "@type": "Organization", name: "Moebix.de", url: "https://www.moebix.de" },
        mainEntityOfPage: { "@type": "WebPage", "@id": `https://www.moebix.de/magazin/${slug.value}` },
      }),
    }];
  }),
});
</script>

<style scoped>
.article-hero {
  position: relative;
  max-height: 400px;
  overflow: hidden;
}

.article-hero__img {
  width: 100%;
}

.article-hero__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, transparent 50%, rgba(0, 0, 0, 0.3));
}

.article-page__title {
  font-family: "Play", sans-serif;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.25;
  color: var(--m-on-surface);
}

.article-page__excerpt {
  font-size: 1.125rem;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.6);
  font-style: italic;
}

.article-page__content {
  font-size: 1rem;
  line-height: 1.8;
  color: rgba(0, 0, 0, 0.75);
}

.article-page__content :deep(h2) {
  font-family: "Play", sans-serif;
  font-size: 1.375rem;
  font-weight: 700;
  margin: 2em 0 0.75em;
  color: var(--m-on-surface);
}

.article-page__content :deep(h3) {
  font-family: "Play", sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  margin: 1.5em 0 0.5em;
  color: var(--m-on-surface);
}

.article-page__content :deep(p) {
  margin-bottom: 1em;
}

.article-page__content :deep(ul) {
  padding-left: 1.5em;
  margin: 1em 0;
}

.article-page__content :deep(li) {
  margin-bottom: 0.5em;
}

.article-page__content :deep(strong) {
  color: var(--m-on-surface);
}

.text-decoration-none {
  text-decoration: none;
}

@media (max-width: 600px) {
  .article-page__title {
    font-size: 1.5rem;
  }
}
</style>
